import React, { createContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

export const ThemeContext = createContext({
  themeColor: '#868686',
  setThemeColor: () => {}
});

const buildAkrosFavicon = (color) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 233.59 247.51">
      <rect width="233.59" height="247.51" fill="transparent"/>
      <polygon points="161.4 32.79 124.66 142.04 198.14 142.04 161.4 32.79" fill="${color}"/>
      <polygon points="131.23 161.38 204.71 161.38 233.67 247.43 160.19 247.43 131.23 161.38" fill="${color}"/>
      <polygon points="150.66 -.08 83.37 -.08 .08 247.43 67.37 247.43 150.66 -.08" fill="${color}"/>
    </svg>
  `;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const syncFavicon = (themeColor) => {
  let favicon = document.querySelector('link[rel="icon"]');
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.appendChild(favicon);
  }

  favicon.type = 'image/svg+xml';
  favicon.href = buildAkrosFavicon(themeColor);

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', themeColor);
  }
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  // Initialize themeColor from localStorage, fallback to default
  const getInitialThemeColor = () => {
    if (user?.themeColor) return user.themeColor;
    const stored = localStorage.getItem('themeColor');
    return stored || '#868686';
  };
  const [themeColor, setThemeColor] = useState(getInitialThemeColor);

  // Update themeColor when user changes
  useEffect(() => {
    if (user?.themeColor) {
      setThemeColor(user.themeColor);
      localStorage.setItem('themeColor', user.themeColor);
    } else {
      // If no user or no user.themeColor, try to restore from localStorage
      const stored = localStorage.getItem('themeColor');
      if (stored) {
        setThemeColor(stored);
      } else {
        setThemeColor('#868686');
      }
    }
  }, [user]);

  // Sync themeColor to CSS variables and localStorage
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeColor);
    root.style.setProperty('--color-primary-light', themeColor); // Optionally adjust for lighter shade
    root.style.setProperty('--color-primary-dark', themeColor);  // Optionally adjust for darker shade
    syncFavicon(themeColor);
    localStorage.setItem('themeColor', themeColor);
  }, [themeColor]);

  return (
    <ThemeContext.Provider value={{ themeColor, setThemeColor }}>
      {children}
    </ThemeContext.Provider>
  );
};
